import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  makeStyles,
  OutlinedInput,
} from '@material-ui/core'
import {
  FaceOutlined as FaceOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
} from '@material-ui/icons'

const useStyles = makeStyles((theme) => ({
  buttonProgress: {
    left: '50%',
    marginLeft: -15,
    marginTop: -15,
    position: 'absolute',
    top: '50%',
  },
  formControl: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    display: 'flex',
    '&.rememberMe': {
      margin: theme.spacing(0),
    },
    '& input + fieldset.MuiOutlinedInput-notchedOutline': {
      borderWidth: 2,
      borderColor: theme.palette.primary.main,
    },
  },
  helperText: {
    fontSize: '1rem',
  },
  label: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 'bolder',
    top: -20,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
    '&.Mui-focused': {
      color: theme.palette.secondary.main,
    },
  },
  outlinedInput: {
    marginTop: theme.spacing(1),
  },
}))

const LoginForm = ({ helperText, onSubmit, showLoadingIndicator }) => {
  const classes = useStyles()

  const [campusKey, setCampusKey] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [isLoginDisabled, setIsLoginDisabled] = useState(true)

  useEffect(() => {
    // when loading indicator is showing , we disable the button since a request to login is in progress
    setIsLoginDisabled(!(campusKey.length && password.length) || showLoadingIndicator)
  }, [campusKey, password, showLoadingIndicator])

  // bind listener for login via Enter key
  useEffect(() => {
    const handleOnKeyUp = ({ key }) => {
      if (key === 'Enter' && !isLoginDisabled) {
        onSubmit(campusKey, password, rememberMe)
      }
    }
    document.addEventListener('keyup', handleOnKeyUp)
    return () => document.removeEventListener('keyup', handleOnKeyUp)
  }, [campusKey, isLoginDisabled, password, rememberMe, onSubmit])

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormControl className={classes.formControl}>
          <InputLabel classes={{ root: classes.label }}>Campus Key</InputLabel>
          <OutlinedInput
            autoFocus
            className={classes.outlinedInput}
            color="secondary"
            fullWidth
            onChange={(event) => setCampusKey(event.target.value)}
            placeholder="Enter your Campus Key"
            startAdornment={
              <InputAdornment position="start">
                <FaceOutlinedIcon />
              </InputAdornment>
            }
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl className={classes.formControl}>
          <InputLabel classes={{ root: classes.label }}>Password</InputLabel>
          <OutlinedInput
            className={classes.outlinedInput}
            color="secondary"
            fullWidth
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Enter your password"
            startAdornment={
              <InputAdornment position="start">
                <LockOutlinedIcon />
              </InputAdornment>
            }
            type="password"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl className={clsx(classes.formControl, 'rememberMe')}>
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                color="secondary"
                onChange={(event) => setRememberMe(event.target.checked)}
              />
            }
            label="Remember me"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        {helperText && (
          <FormHelperText className={classes.helperText} error>
            {helperText}
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <FormControl className={classes.formControl} style={{ position: 'relative' }}>
          <Button
            color="primary"
            disabled={isLoginDisabled}
            fullWidth
            onClick={() => onSubmit(campusKey, password, rememberMe)}
            variant="contained"
          >
            Login
          </Button>
          {showLoadingIndicator && (
            <CircularProgress className={classes.buttonProgress} size={30} />
          )}
        </FormControl>
      </Grid>
    </Grid>
  )
}

LoginForm.defaultProps = {
  helperText: '',
  showLoadingIndicator: false,
}

LoginForm.propTypes = {
  helperText: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  showLoadingIndicator: PropTypes.bool,
}

export default LoginForm
